// Usage:
// <ProfileCard viewUserId={someUserId} onFollowChange={(id, isFollowing) => {}} />
// or for current user: <ProfileCard />

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, onSnapshot, collection, query, orderBy, limit, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import useUserDoc from '../hooks/useUserDoc';
import Badges from './Badges';

/**
 * ProfileCard
 * - Shows a user's profile with live updates from Firestore (user doc + recentTips subcollection)
 * - Renders action buttons depending on whether it's the current user or another user
 */
export default function ProfileCard({ viewUserId, onFollowChange }) {
    const navigate = useNavigate();
    // Resolve target user id (fallback to current user)
    const resolvedUserId = useMemo(() => viewUserId || (auth.currentUser && auth.currentUser.uid) || null, [viewUserId]);

    const { user: userDoc, loading, error } = useUserDoc(resolvedUserId);

    // Recent tips realtime state
    const [recentTips, setRecentTips] = useState([]);

    // Follow state
    const [isFollowing, setIsFollowing] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);

    // Simple in-component toast
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    // Subscribe to recent tips (limit 3, newest first)
    useEffect(() => {
        if (!resolvedUserId) return () => {};
        const tipsCol = collection(db, 'users', resolvedUserId, 'recentTips');
        const q = query(tipsCol, orderBy('createdAt', 'desc'), limit(3));
        const unsub = onSnapshot(q, (snap) => {
            const tips = [];
            snap.forEach((d) => tips.push({ id: d.id, ...d.data() }));
            setRecentTips(tips);
        }, (e) => console.error('recentTips onSnapshot error', e));
        return () => unsub();
    }, [resolvedUserId]);

    // Resolve initial follow state (does current user follow this profile?)
    useEffect(() => {
        const run = async () => {
            try {
                if (!resolvedUserId || !auth.currentUser || auth.currentUser.uid === resolvedUserId) {
                    setIsFollowing(false);
                    return;
                }
                const followerDocRef = doc(db, 'users', resolvedUserId, 'followers', auth.currentUser.uid);
                const followerSnap = await getDoc(followerDocRef);
                setIsFollowing(followerSnap.exists());
            } catch (e) {
                console.error('Failed to resolve follow state', e);
            }
        };
        run();
    }, [resolvedUserId]);

    // Helpers
    const isOwnProfile = auth.currentUser && resolvedUserId && auth.currentUser.uid === resolvedUserId;

    const showToast = (text) => {
        setToast(text);
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 1800);
    };

    const handleShare = async () => {
        try {
            const link = `${window.location.origin}/profile/${resolvedUserId}`;
            await navigator.clipboard.writeText(link);
            showToast('Link copied');
        } catch (e) {
            console.error('Share failed', e);
            showToast('Failed to copy');
        }
    };

    const toggleFollow = async () => {
        if (!auth.currentUser || !resolvedUserId || isOwnProfile) return;
        setFollowBusy(true);
        try {
            const followerDocRef = doc(db, 'users', resolvedUserId, 'followers', auth.currentUser.uid);
            if (isFollowing) {
                await deleteDoc(followerDocRef);
                setIsFollowing(false);
                onFollowChange && onFollowChange(resolvedUserId, false);
            } else {
                await setDoc(followerDocRef, { uid: auth.currentUser.uid, createdAt: Date.now() });
                setIsFollowing(true);
                onFollowChange && onFollowChange(resolvedUserId, true);
            }
        } catch (e) {
            console.error('Follow toggle failed', e);
            showToast('Action failed');
        } finally {
            setFollowBusy(false);
        }
    };

    // Render states
    if (!resolvedUserId) {
        return (
            <div className="w-full max-w-xl mx-auto p-4 text-center text-red-400">No user specified.</div>
        );
    }

    if (loading) {
        return (
            <div className="w-full max-w-xl mx-auto p-6 rounded-xl bg-neutral-900/60 text-neutral-300 animate-pulse">Loading profile…</div>
        );
    }

    if (error) {
        return (
            <div className="w-full max-w-xl mx-auto p-6 rounded-xl bg-neutral-900/60 text-red-400">Error loading profile.</div>
        );
    }

    if (!userDoc) {
        return (
            <div className="w-full max-w-xl mx-auto p-6 rounded-xl bg-neutral-900/60 text-neutral-300">User not found</div>
        );
    }

    const displayName = userDoc.displayName || 'Unknown';
    const username = userDoc.username || null;
    const photoURL = userDoc.photoURL || undefined;
    const role = userDoc.role || 'listener';
    const followersCount = typeof userDoc.followersCount === 'number' ? userDoc.followersCount : 0;
    const followingCount = typeof userDoc.followingCount === 'number' ? userDoc.followingCount : 0;
    const totalTips = typeof userDoc.totalTips === 'number' ? userDoc.totalTips : 0;
    const badges = Array.isArray(userDoc.badges) ? userDoc.badges : [];

    return (
        <div className="w-full max-w-2xl mx-auto">
            {/* Toast */}
            {toast && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-neutral-900/90 text-white px-3 py-1.5 rounded-full text-sm shadow-lg z-50">
                    {toast}
                </div>
            )}

            {/* Card */}
            <div className="rounded-2xl bg-neutral-900/70 backdrop-blur border border-white/5 p-5 md:p-6 text-white shadow-[0_0_40px_rgba(255,0,0,0.06)]">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <img
                        src={photoURL || 'https://ui-avatars.com/api/?background=111111&color=ffffff&name=' + encodeURIComponent(displayName)}
                        alt={displayName}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-2 ring-red-600/30"
                        loading="lazy"
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h2 className="text-2xl font-bold tracking-tight">{displayName}</h2>
                            <span className="text-xs px-2 py-0.5 rounded bg-white/10 uppercase">
                                {role}
                            </span>
                        </div>
                        <div className="text-neutral-400 text-sm">{username ? `@${username}` : resolvedUserId}</div>
                    </div>

                    {/* Actions */}
                    {isOwnProfile ? (
                        <div className="hidden md:flex items-center gap-2">
                            <button
                                className="px-4 py-2 rounded-lg bg-red-600 text-white shadow-[0_0_20px_rgba(255,0,0,0.35)] hover:shadow-[0_0_32px_rgba(255,0,0,0.55)] transition-all"
                                onClick={() => window.dispatchEvent(new CustomEvent('open-profile-editor'))}
                            >
                                Edit Profile
                            </button>
                            <button
                                className="px-4 py-2 rounded-lg bg-white text-black hover:bg-neutral-100 transition-all"
                                onClick={handleShare}
                            >
                                Share Profile
                            </button>
                        </div>
                    ) : (
                        <div className="hidden md:flex items-center gap-2">
                            <button
                                className="px-4 py-2 rounded-lg bg-red-600 text-white shadow-[0_0_20px_rgba(255,0,0,0.35)] hover:shadow-[0_0_32px_rgba(255,0,0,0.55)] transition-all disabled:opacity-60"
                                onClick={toggleFollow}
                                disabled={followBusy}
                            >
                                {isFollowing ? 'Unfollow' : 'Follow'}
                            </button>
                            <button
                                className="px-4 py-2 rounded-lg bg-white text-black hover:bg-neutral-100 transition-all"
                                onClick={() => navigate(`/messages?to=${resolvedUserId}`)}
                            >
                                Message
                            </button>
                        </div>
                    )}
                </div>

                {/* Mobile actions */}
                <div className="mt-4 flex md:hidden gap-2">
                    {isOwnProfile ? (
                        <>
                            <button
                                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white shadow-[0_0_20px_rgba(255,0,0,0.35)] hover:shadow-[0_0_32px_rgba(255,0,0,0.55)] transition-all"
                                onClick={() => window.dispatchEvent(new CustomEvent('open-profile-editor'))}
                            >
                                Edit Profile
                            </button>
                            <button className="flex-1 px-4 py-2 rounded-lg bg-white text-black hover:bg-neutral-100" onClick={handleShare}>Share</button>
                        </>
                    ) : (
                        <>
                            <button
                                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white shadow-[0_0_20px_rgba(255,0,0,0.35)] hover:shadow-[0_0_32px_rgba(255,0,0,0.55)] transition-all disabled:opacity-60"
                                onClick={toggleFollow}
                                disabled={followBusy}
                            >
                                {isFollowing ? 'Unfollow' : 'Follow'}
                            </button>
                            <button
                                className="flex-1 px-4 py-2 rounded-lg bg-white text-black hover:bg-neutral-100"
                                onClick={() => navigate(`/messages?to=${resolvedUserId}`)}
                            >
                                Message
                            </button>
                        </>
                    )}
                </div>

                {/* Stats */}
                <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                    <div className="rounded-lg bg-white/5 py-3">
                        <div className="text-2xl font-bold">{followingCount}</div>
                        <div className="text-xs text-neutral-400">Following</div>
                    </div>
                    <div className="rounded-lg bg-white/5 py-3">
                        <div className="text-2xl font-bold">{followersCount}</div>
                        <div className="text-xs text-neutral-400">Followers</div>
                    </div>
                    <div className="rounded-lg bg-white/5 py-3">
                        <div className="text-2xl font-bold">{totalTips}</div>
                        <div className="text-xs text-neutral-400">Total tips</div>
                    </div>
                </div>

                {/* Badges */}
                {badges.length > 0 && (
                    <div className="mt-6">
                        <Badges badges={badges} />
                    </div>
                )}

                {/* Recent tips */}
                <div className="mt-6">
                    <h3 className="font-semibold mb-2">Recent tips</h3>
                    {recentTips.length === 0 ? (
                        <div className="text-sm text-neutral-400">—</div>
                    ) : (
                        <ul className="space-y-2">
                            {recentTips.map((t) => (
                                <li key={t.id} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2">
                                    <span className="text-sm">{t.name || 'Anonymous'}</span>
                                    <span className="text-sm font-semibold">{typeof t.amount === 'number' ? `₹${t.amount}` : ''}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}


